import React, { FC, useCallback, useState } from 'react';
import { Button, Dropdown, Icons, Search } from '@silverfort/sf-components';
import { LastAccessOptions } from '../../../common/lastAccess';
import { ResourceFilters } from '../../../types/ResourceFilters';
import { ResourceTypeOptions } from '../../../common/resourceType';
import { ResourcesOptionsMenu } from './ResourcesOptionsMenu';
import { useGetResourcesReport } from '../../../api/queries/useGetResourcesReport';
import { Sorting } from '../../../types/Sorting';
import { StaleSettingsModal } from './StaleSettingsModal';
import { useUpdateDefaultStaleSetting } from '../../../api/mutations/useUpdateDefaultStaleSetting';
import { useGetDefaultStaleAccessSetting } from '../../../api/queries/useGetDefaultStaleAccessSetting';

interface ResourcesQueryBuilderProps {
    filters: ResourceFilters;
    showExport?: boolean;
    sorting: Sorting;
    updateFilter: <K extends keyof ResourceFilters>(field: K, value: ResourceFilters[K]) => void;
}

export const ResourcesQueryBuilder: FC<ResourcesQueryBuilderProps> = ({
    filters,
    showExport,
    sorting,
    updateFilter
}) => {
    const [searchText, setSearchText] = useState('');
    const [staleSettingsModalOpen, setStaleSettingsModalOpen] = useState(false);

    const { downloadReport, downloadingReport } = useGetResourcesReport(filters, sorting);

    const { defaultStaleAccessSetting, refetch: refetchDefaultStaleSettings } = useGetDefaultStaleAccessSetting();
    const { updateDefaultStaleSetting } = useUpdateDefaultStaleSetting(refetchDefaultStaleSettings);

    const onSearch = useCallback(
        (text?: string) => {
            setSearchText(text ?? '');

            updateFilter('name', text ?? '');
        },
        [updateFilter]
    );

    const updateLastAccess = useCallback(
        (value?: number | Array<number>) => {
            const val = Array.isArray(value) ? value.at(0) : value;

            updateFilter('lastAccessInDays', val);
        },
        [updateFilter]
    );

    const updateType = useCallback(
        (value?: string | Array<string>) => {
            const val = Array.isArray(value) ? value.at(0) : value;

            updateFilter('type', val);
        },
        [updateFilter]
    );

    return (
        <>
            <section className="flex justify-between">
                <div className="flex gap-2">
                    <Dropdown
                        key="type"
                        items={ResourceTypeOptions}
                        onChange={updateType}
                        onRemove={updateType}
                        removable
                        title="Type"
                        variant="filter"
                    />

                    <Dropdown
                        key="lastAccessInDays"
                        items={LastAccessOptions}
                        onChange={updateLastAccess}
                        onRemove={updateLastAccess}
                        removable
                        selectedItems={filters.lastAccessInDays}
                        title="Last login before"
                        variant="filter"
                    />
                </div>

                <div className="flex gap-2">
                    <Search border onChange={onSearch} placeholder="Search" text={searchText} />

                    {showExport && (
                        <Button
                            iconRight={Icons.Download}
                            isLoading={downloadingReport}
                            onClick={downloadReport}
                            title="Export Resources"
                        />
                    )}

                    <ResourcesOptionsMenu onStaleSettingsUpdateClick={() => setStaleSettingsModalOpen(true)} />
                </div>
            </section>

            <StaleSettingsModal
                currentState={defaultStaleAccessSetting}
                onApprove={updateDefaultStaleSetting}
                onClose={() => setStaleSettingsModalOpen(false)}
                open={staleSettingsModalOpen}
            />
        </>
    );
};
